import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Trash2, Eye, Pencil } from "lucide-react";

import DashboardLayout from "../Layouts/DashboardLayout";
import ProtectedRoute from "../Components/ProtectedRoute";
import JournalModal from "./JournalModal";
import ViewJournalModal from "./ViewJournalModal";

export default function JournalEntries() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    debit: 0,
    credit: 0,
  });
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewJournalId, setViewJournalId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [journalRes, summaryRes, accountRes] = await Promise.all([
        fetch("/api/journal", {
          credentials: "include",
        }),

        fetch("/api/journal/summary", {
          credentials: "include",
        }),

        fetch("/api/accounts", {
          credentials: "include",
        }),
      ]);

      const journalData = await journalRes.json();
      const summaryData = await summaryRes.json();
      const accountData = await accountRes.json();

      if (journalData.status === "success") {
        setEntries(journalData.data);
      }

      if (summaryData.status === "success") {
        setStats(summaryData.data);
      }

      if (accountData.status === "success") {
        setAccounts(accountData.data);
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function deleteJournal(id) {
    if (!window.confirm("Delete Journal?")) return;

    const res = await fetch(`/api/journal/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      loadData();
    } else {
      alert(data.message);
    }
  }

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      return (
        e.entry_no.toLowerCase().includes(search.toLowerCase()) ||
        (e.reference || "").toLowerCase().includes(search.toLowerCase()) ||
        (e.narration || "").toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [entries, search]);

  return (
    <ProtectedRoute>
      <DashboardLayout>
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif">Journal Entries</h1>

              <p className="text-gray-400 mt-1">
                Manage accounting journal vouchers
              </p>
            </div>

            <button
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 bg-amber-400 text-black px-5 py-2 rounded-xl"
            >
              <Plus size={18} />
              New Journal
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Kpi title="Entries" value={stats.total} />

            <Kpi
              title="Debit"
              value={`₹${Number(stats.debit).toLocaleString("en-IN")}`}
            />

            <Kpi
              title="Credit"
              value={`₹${Number(stats.credit).toLocaleString("en-IN")}`}
            />
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <div className="relative max-w-md">
              <Search
                size={16}
                className="absolute left-3 top-3 text-gray-500"
              />

              <input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-black/40 border border-white/10"
              />
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-6 py-4 text-left">Voucher</th>

                  <th className="px-6 py-4 text-left">Date</th>

                  <th className="px-6 py-4 text-left">Reference</th>

                  <th className="px-6 py-4 text-right">Debit</th>

                  <th className="px-6 py-4 text-right">Credit</th>

                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((j) => (
                  <tr
                    key={j.id}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="px-6 py-4 font-medium">{j.entry_no}</td>

                    <td className="px-6 py-4">{j.entry_date}</td>

                    <td className="px-6 py-4">{j.reference}</td>

                    <td className="px-6 py-4 text-right">
                      ₹{Number(j.debit).toLocaleString("en-IN")}
                    </td>

                    <td className="px-6 py-4 text-right">
                      ₹{Number(j.credit).toLocaleString("en-IN")}
                    </td>

                    <td>
                      <div className="flex justify-center gap-3">
                        <button onClick={() => setViewJournalId(j.id)}>
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => {
                            setEditing(j);
                            setShowModal(true);
                          }}
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          onClick={() => deleteJournal(j.id)}
                          className="text-red-400"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <JournalModal
            editing={editing}
            accounts={accounts}
            onClose={() => {
              setShowModal(false);
              loadData();
            }}
          />
        )}

        {viewJournalId && (
          <ViewJournalModal
            journalId={viewJournalId}
            onClose={() => setViewJournalId(null)}
          />
        )}
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function Kpi({ title, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2 className="mt-3 text-3xl font-serif">{value}</h2>
    </div>
  );
}
